import { useQuery, useQueryCache } from 'react-query';
import React, { useReducer, useState, useCallback } from 'react';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import { QueryKey } from 'api/queryKey';
import Column from 'antd/lib/table/Column';
import { Button, Dropdown, Menu, message } from 'antd';
import { useEnsureContext } from 'common/hooks';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { Header } from 'common/components/Header';
import { BusinessPartnerDoctorsManagementRoutes } from 'pages/BusinessPartnerManagement/BusinessPartnerDoctorsManagement/businessPartnerDoctorsManagementRoutes';
import { useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { purge } from 'lib/ramda/purge';
import { MedicalOperatorsFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/MedicalOperatorsFilters';
import { DeleteMedicalOperatorButton } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/DeleteMedicalOperatorButton';
import { MedicalOperatorDTO } from 'models/medicalOperator';
import { UserStatusTag } from 'common/components/UserStatusTag';
import { DeleteOutlined, RedoOutlined, TeamOutlined } from '@ant-design/icons';
import { RequestState, useRequestsState } from 'common/hooks/useRequestsState';
import { UserStatus } from 'models/userStatus';
import { DeleteUserStatus } from 'models/deleteUserStatus';
import { AdministrationGuard } from 'common/components/AdministrationGuard';
import { useRole } from 'common/hooks/useRole';
import { medicalOperatorsModalReducer } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/utils/medicalOperatorsModalReducer';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';
import { NumberedTable } from 'common/components/NumberedTable';
import { toSimpleDate } from 'common/dateFnsUtils';
import { UsersActivateButton } from 'pages/BusinessPartnerManagement/UsersActivateButton';
import { makeSubpartnersIdsString } from 'pages/BusinessPartnerManagement/BusinessPartnerMedicalOperatorsManagement/BusinessPartnerMedicalOperatorsPage/utils/makeSubpartnersIDsString';
import { isNil } from 'ramda';

export const BusinessPartnerMedicalOperatorsPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const queryCache = useQueryCache();
  const { url } = useRouteMatch();
  const { isAdministrationRole } = useRole();
  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [filters, setFilters] = useState({});
  const [filteredPartnerId, setFilteredPartnerId] = useState(0);
  const [operatorsCanBeFetched, setOperatorsCanBeFetched] = useState(false);

  const { isMedicalOperator } = useRole();
  const shouldHidePartnerColumn =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;
  const [modalState] = useReducer(medicalOperatorsModalReducer, {
    medicalOperator: undefined,
    operationVisible: undefined,
  });
  const intlMessage = useFormatMessage();

  const IDlist: number[] = [];
  const [IDsToGetData, setIDsToGetData] = useState('');
  const invalidateMedicalOperators = useCallback(
    () =>
      queryCache
        .invalidateQueries(QueryKey.BusinessPartnerMedicalOperatorsList)
        .then(),
    [queryCache]
  );

  const {
    isLoading: isLoadingSubpartners,
    isSuccess: isSubpartnersSuccess,
  } = useQuery(
    [QueryKey.BusinessPartnersList],
    () => BusinessPartnersAPI.fetchSubpartners(bpContext.id),
    {
      onSuccess: (res) => {
        const IDsString = makeSubpartnersIdsString(res, IDlist);
        setIDsToGetData(IDsString);
        invalidateMedicalOperators();
        setOperatorsCanBeFetched(true);
      },
      keepPreviousData: true,
    }
  );

  const { isFetching, data } = useQuery(
    [
      QueryKey.BusinessPartnerMedicalOperatorsList,
      pageMetaData,
      filters,
      IDsToGetData,
    ],
    () =>
      BusinessPartnersAPI.fetchMedicalOperators(
        filteredPartnerId !== 0 ? filteredPartnerId : bpContext.id,
        {
          subPartnersIds: filteredPartnerId !== 0 ? '' : IDsToGetData,
          ...pageMetaData,
          ...filters,
        }
      ),
    {
      enabled: isSubpartnersSuccess && operatorsCanBeFetched,
      keepPreviousData: true,
    }
  );

  const [state, resetPassword] = useRequestsState((d: MedicalOperatorDTO) =>
    BusinessPartnersAPI.resetDoctorPassword({
      businessPartnerId: bpContext.id,
      id: d.userId,
    }).then(() => {
      message.success(
        intlMessage(
          { id: 'person.passwordReset.sent' },
          {
            name: `${d.firstName} ${d.lastName}`,
          }
        )
      );
      queryCache.invalidateQueries(
        QueryKey.BusinessPartnerMedicalOperatorsList
      );
    })
  );

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  return (
    <>
      <Header
        title={intlMessage(
          { id: 'medicalOperators' },
          {
            firstName: modalState.medicalOperator?.firstName,
            lastName: modalState.medicalOperator?.lastName,
          }
        )}
        extra={[
          <AdministrationGuard
            key={`${url}/${BusinessPartnerDoctorsManagementRoutes.Register}`}
          >
            <Link
              id="button-add-new-medical-operator"
              className="ant-btn ant-btn-primary"
              to={`${url}/${BusinessPartnerDoctorsManagementRoutes.Register}`}
            >
              <FormattedMessage id="medicalOperators.addNew" />
            </Link>
          </AdministrationGuard>,
        ]}
      />
      <MedicalOperatorsFilters
        shouldHidePartnerColumn={shouldHidePartnerColumn}
        onSubmit={(values) => {
          (values.partnerID === 0 || !values.partnerID) &&
            setFilteredPartnerId(0);
          values.partnerID && setFilteredPartnerId(values.partnerID);
          setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
          setFilters(purge(values));
        }}
      />
      <NumberedTable<MedicalOperatorDTO>
        rowKey="id"
        loading={isLoadingSubpartners || isFetching}
        dataSource={data?.content}
        pagination={paginationConfig}
        scroll={{ x: 'auto' }}
      >
        {!isLoadingSubpartners && !isFetching && (
          <>
            <Column
              title={intlMessage({ id: 'name' })}
              dataIndex="firstName"
              render={(value, record: MedicalOperatorDTO) => (
                <Link
                  key={`${url}/${record.userId}`}
                  to={`${url}/${record.userId}`}
                >
                  {record.firstName + ' ' + record.lastName}
                </Link>
              )}
            />
            <Column title="Email/Login" dataIndex="email" />
            {!shouldHidePartnerColumn && (
              <Column
                title={intlMessage({ id: 'partner' })}
                dataIndex={['partnerDetails', 'name']}
              />
            )}
            <Column
              title={intlMessage({ id: 'create_date' })}
              dataIndex="createDate"
              render={(value) => toSimpleDate(value)}
            />
            <Column
              title={intlMessage({ id: 'familyUsers.lastLogin' })}
              dataIndex="lastLoginDate"
              render={(value) => (value === null ? '--' : toSimpleDate(value))}
              align="center"
            />
            <Column
              title={intlMessage({ id: 'deviceSerialNumber' })}
              dataIndex="device"
              render={(value) => value?.serialNumber}
            />
            <Column
              title={intlMessage({ id: 'status' })}
              dataIndex="status"
              render={(value) => <UserStatusTag status={value} />}
            />

            <Column
              title={intlMessage({ id: 'actions' })}
              key="actions"
              align="right"
              render={(value, record: MedicalOperatorDTO) => {
                const notArchived = record.status !== UserStatus.Archived;
                const notDeactivated = record.status !== UserStatus.Deactivated;
                const notDeleted = record.status !== UserStatus.Deleted;
                const isDeactivated = record.status === UserStatus.Deactivated;

                return (
                  <Dropdown.Button
                    overlay={
                      <Menu>
                        {isAdministrationRole && [
                          notDeleted && notDeactivated && (
                            <Menu.Item
                              key="patient-management"
                              id="menu-item-patient-management-medical-operator"
                            >
                              <Button
                                id="button-medical-operator-patient-management"
                                type="text"
                                icon={<TeamOutlined />}
                              >
                                <Link
                                  style={{ color: 'black', marginLeft: '7px' }}
                                  key={`${url}/${record.userId}`}
                                  to={{
                                    pathname: `${url}/${record.userId}`,
                                    state: {},
                                  }}
                                >
                                  <FormattedMessage
                                    id="medicalOperators.patientsManagement"
                                    tagName="span"
                                  />
                                </Link>
                              </Button>
                            </Menu.Item>
                          ),

                          notDeleted && notDeactivated && (
                            <Menu.Item
                              key="reset-password"
                              id="menu-item-reset-password-medical-operator"
                            >
                              <Button
                                id="button-reset-medical-operator-password"
                                type="text"
                                icon={<RedoOutlined />}
                                loading={
                                  RequestState.Pending === state[record.userId]
                                }
                                onClick={() =>
                                  resetPassword(record.userId, record)
                                }
                              >
                                <FormattedMessage
                                  id="person.passwordReset"
                                  tagName="span"
                                />
                              </Button>
                            </Menu.Item>
                          ),

                          notDeactivated && notArchived && notDeleted && (
                            <Menu.Item
                              key="deactivate"
                              id="menu-item-deactivate-medical-operator"
                            >
                              <UsersActivateButton
                                actionType="deactivate"
                                userId={record.userId}
                                type="text"
                                userName={`${record.firstName} ${record.lastName}`}
                                onStatusChange={invalidateMedicalOperators}
                              >
                                <FormattedMessage
                                  id="businessPartners.deactivate"
                                  tagName="span"
                                />
                              </UsersActivateButton>
                            </Menu.Item>
                          ),
                          isDeactivated && (
                            <Menu.Item
                              key="activate"
                              id="menu-item-activate-medical-operator"
                            >
                              <UsersActivateButton
                                actionType="activate"
                                userId={record.userId}
                                userName={`${record.firstName} ${record.lastName}`}
                                type="text"
                                onStatusChange={invalidateMedicalOperators}
                              >
                                <FormattedMessage
                                  id="businessPartners.activate"
                                  tagName="span"
                                />
                              </UsersActivateButton>
                            </Menu.Item>
                          ),

                          notDeleted && (
                            <Menu.Item
                              key="remove"
                              id="menu-item-remove-medical-operator"
                            >
                              <DeleteMedicalOperatorButton
                                danger
                                type="text"
                                businessPartnerId={bpContext.id}
                                medicalOperator={record}
                                status={DeleteUserStatus.Delete}
                                tooltip={null}
                                icon={<DeleteOutlined />}
                                onDeleted={() => {
                                  queryCache
                                    .invalidateQueries(
                                      QueryKey.BusinessPartnerMedicalOperatorsList
                                    )
                                    .then();
                                }}
                              >
                                <FormattedMessage id="delete" tagName="span" />
                              </DeleteMedicalOperatorButton>
                            </Menu.Item>
                          ),
                        ]}
                      </Menu>
                    }
                  />
                );
              }}
            />
          </>
        )}
      </NumberedTable>
    </>
  );
};
